/* =====================================================================
   NL5 core — shared by nl5-quiz.js, nl5-pdp.js, nl5-cart.js (and node tests).
   1) createReco(D)   : recommendation engine, ported VERBATIM from mock/quiz-popup.html NL_RECO
                        (only changes: D is passed in; product URLs / images come from the catalog).
   2) applyCatalog()  : overwrite D.prices_cad / D.in_stock / D.tier_availability from Liquid-injected
                        window.NL5_PRODUCTS (real variant.price / variant.available / selling plans).
   3) helpers         : money, i18n, dataLayer, localStorage, cart AJAX, Minimog drawer refresh, email capture.
   Design v1.4 appendix A/B/D. No DOM access in sections 1–2 (node-testable).
   ===================================================================== */
(function (root) {
  'use strict';
  if (root.NL5 && root.NL5.__core) return;
  var NL5 = root.NL5 = root.NL5 || {};
  NL5.__core = true;

  /* ---------------------------------------------------------------
     1) RECOMMENDATION ENGINE (pure). Returns i18n keys; UI renders text.
     --------------------------------------------------------------- */
  NL5.createReco = function (D, env) {
    env = env || {};
    var HAS_WEEKLY_PLANS = D.has_weekly_plans;
    function setWeekly(v) { HAS_WEEKLY_PLANS = !!v; }
    function r2(n) { return Math.round(n * 100) / 100; }
    function catsN(c) { return c === '4+' ? 4 : Math.max(1, Math.min(4, parseInt(c, 10) || 1)); }
    function cnt(v) { return v === '3+' ? 3 : (parseInt(v, 10) || 1); }
    function boxesN(a) { return a.boxes === 'auto' ? cnt(a.auto_count || '1') : cnt(a.boxes); }
    function q3Of(a) {
      if (a.current_litter === 'clay') return a.plant_tried ? 'clay_tried' : 'clay_never';
      return a.current_litter; // 'crystal_other' | 'tofu' | 'cassava'
    }
    function tier(key) { for (var i = 0; i < D.tiers.length; i++) if (D.tiers[i].key === key) return D.tiers[i]; return null; }
    // split (10/12): the 1-bag part of a 7-bag order (6-bag case + 1 bag) rides the order's subscription → the tiers of bags.split_tier_from
    function tiersFor(L, Z, split) {
      var Z2 = Z;
      if (D.bags && (D.bags.one_time_only || []).indexOf(Z) >= 0) { if (!split) return []; Z2 = (D.bags.split_tier_from || {})[Z] || '2pk'; }   // B (10/9): 1 bag alone is a one-time trial
      var keys = ((D.tier_availability[L] || {})[Z2] || []).slice();
      if (HAS_WEEKLY_PLANS) keys = keys.concat(D.weekly_tiers_added_everywhere || []);
      return D.tiers.filter(function (t) { return keys.indexOf(t.key) >= 0; });
    }
    function daysOfUse(Z, n, upper) { return D.sizes[Z].kg / (upper ? D.monthly_kg_upper : D.monthly_kg)[n] * D.days_per_month; }
    function monthsOfUse(Z, n) { return D.sizes[Z].kg / D.monthly_kg[n]; }
    // ④ closest tier to the days one box lasts at the UPPER usage; ties → shorter tier
    function freqFor(L, Z, n) {
      if (daysOfUse(Z, n) < D.min_days_to_subscribe) return null;
      var target = daysOfUse(Z, n, true), best = null;
      tiersFor(L, Z).forEach(function (t) {
        if (t.days - target > D.max_early_days) return;
        var d = Math.abs(t.days - target);
        if (!best || d < best.d - 0.05 || (Math.abs(d - best.d) <= 0.05 && t.days < best.t.days)) best = { t: t, d: d };
      });
      return best ? best.t.key : null;
    }
    // at upper usage the box runs out on day floor(days); delivery arrives on round(tier days) → N days early (0 = fine)
    function earlyDays(Z, n, freq) {
      var gap = Math.round(tier(freq).days) - Math.floor(daysOfUse(Z, n, true));
      return gap > 0 ? gap : 0;
    }
    function inStock(L, S, Z, stock) { var s = stock || D.in_stock; return !!(s[L] && s[L][S] && s[L][S][Z]); }
    function price(L, S, Z) { return D.prices_cad[L][S][Z]; }
    // theme: real subscription price (selling_plan_allocation) when the catalog provides one
    function subPrice(L, S, Z) {
      var sp = D.sub_prices_cad && D.sub_prices_cad[L] && D.sub_prices_cad[L][S] && D.sub_prices_cad[L][S][Z];
      return sp != null ? sp : r2(price(L, S, Z) * (1 - D.sub_discount));
    }
    function perCatMonth(L, S, Z, n) { return r2(subPrice(L, S, Z) / n / monthsOfUse(Z, n)); }
    function matches(cond, ctx) { for (var k in cond) if (ctx[k] !== cond[k]) return false; return true; }
    function firstFill(L, S, a, stock) {
      if (D.first_fill.applies_if_q3_in.indexOf(q3Of(a)) < 0) return null;
      var boxes = boxesN(a), want = boxes >= 2 ? D.first_fill.two_plus_boxes : D.first_fill.one_box;
      var order = [want].concat(D.first_fill.fallback_order.filter(function (z) { return z !== want && D.sizes[z].kg >= D.sizes[want].kg; }));
      for (var i = 0; i < order.length; i++) if (inStock(L, S, order[i], stock))
        return { size: order[i], qty: 1, price: price(L, S, order[i]), boxes: boxes, kg_per_box: D.first_fill.kg_per_box_label[L], wanted: want, auto_add: false };
      return null;
    }
    function image(L, S, Z) { return env.image ? env.image(L, S, Z) : null; }
    function pdpUrl(L, S, Z, n, plan, fill) {
      var base = env.pdpPath ? env.pdpPath(L, S, Z) : '/products/' + (L === 'cassava' ? 'cassava-cat-litter-cubic-pack' : 'vanilla-white-tea-tofu-cat-litter-canada');
      return base + (base.indexOf('?') >= 0 ? '&' : '?') + 'cats=' + n + '&size=' + Z + '&plan=' + plan + '&scent=' + S + (fill ? '&fill=' + fill.size + '&fill_qty=' + fill.qty : '');
    }

    /**
     * answers: { cats:'1'|'2'|'3'|'4+', boxes:'1'|'2'|'3+'|'auto',
     *            auto_box:'litter-robot'|'neakasa'|'catlink'|'petkit'|'other'|null, auto_count:'1'|'2'|'3+'|null,
     *            current_litter:'clay'|'crystal_other'|'tofu'|'cassava', plant_tried:true|false|null,
     *            tried_which:'tofu'|'cassava'|'corn-wood-paper'|'not-sure'|null,
     *            problem:'smell'|'dust_human'|'cat_sensitive'|'clumps'|'tracking'|'picky' }
     * opts: { litter?, scent?, useAlt?, stock?, fillAdded? }
     */
    function recommend(a, o) {
      o = o || {};
      var n = catsN(a.cats), stock = o.stock || D.in_stock, q3 = q3Of(a);
      var autoBox = a.boxes === 'auto' ? a.auto_box : null;
      var ctx = { auto_box: autoBox, q3: q3, problem: a.problem, tried_which: a.tried_which || null };
      // ② LITTER — first matching rule
      var rule = D.litter_rules.filter(function (r) { return matches(r.if, ctx); })[0];
      var ruleLitter = rule.litter === 'BY_PROBLEM' ? (D.problem_litter[a.problem] || 'tofu') : rule.litter;
      var why = rule.why.map(function (k) { return k === 'BY_PROBLEM' ? (D.problem_why[a.problem] || 'why_smell') : k; });
      var tips = (rule.tips || []).slice();
      if (rule.tips_if_lr && autoBox === 'litter-robot') tips = tips.concat(rule.tips_if_lr);
      var probLitter = D.problem_litter[a.problem];
      var lrVeto = ['litter_robot', 'neakasa', 'catlink'].indexOf(rule.id) >= 0;   // auto-box vetoes never point back to tofu
      if (rule.id === 'by_problem') tips = tips.concat(D.problem_tips_same_litter[a.problem] || []);
      else if (lrVeto) { if (a.problem === 'tracking') tips.push('tip_tracking'); }       // never point an auto-box veto home back to tofu
      else if (probLitter && probLitter !== ruleLitter) tips = tips.concat(D.problem_tips_other_litter[a.problem] || []);
      else if (a.problem === 'tracking') tips.push('tip_tracking');
      if (autoBox && autoBox !== 'litter-robot' && rule.id !== 'neakasa' && rule.id !== 'catlink') tips.push(D.auto_box_generic_tip);
      if (a.boxes !== 'auto' && boxesN(a) < n) tips.push(D.box_rule_tip.tip);
      if (String(n) === D.big_household_tip.cats) tips.push(D.big_household_tip.tip);
      // ③ SIZE (cat count) filtered by ① STOCK; ④ must be subscribable
      var sizes = D.size_order_by_cats[String(n)];
      var wantLitter = o.litter || ruleLitter;
      var litters = [wantLitter, wantLitter === 'tofu' ? 'cassava' : 'tofu'];
      var cands = [];
      litters.forEach(function (L) {
        var scents = D.scent_order[L].slice();
        if (o.scent && scents.indexOf(o.scent) >= 0)   /* 10/9: scent preference also when tofu is the fallback litter */ scents = [o.scent].concat(scents.filter(function (s) { return s !== o.scent; }));
        scents.forEach(function (S) { sizes.forEach(function (Z) { if (freqFor(L, Z, n)) cands.push({ litter: L, scent: S, size: Z }); }); });
      });
      var avail = cands.filter(function (c) { return inStock(c.litter, c.scent, c.size, stock); });
      var ideal = cands[0];
      if (!avail.length) return { none: true, stock_note: 'stock_none', cats_n: n, q3: q3, rule: rule.id };
      var primary = avail[0];
      var sameLS = avail.filter(function (c) { return c.litter === primary.litter && c.scent === primary.scent; });
      var other = D.no_other_option_for_cats.indexOf(String(n)) >= 0 ? null : (sameLS[1] || null);
      if (o.useAlt && other) { var t = primary; primary = other; other = t; }
      var stockNote = null;
      if (primary.litter !== ideal.litter) stockNote = 'stock_litter';
      else if (primary.scent !== ideal.scent) stockNote = 'stock_scent';
      else if (!o.useAlt && primary.size !== ideal.size) stockNote = 'stock_size';
      var L = primary.litter, S = primary.scent, Z = primary.size, freq = freqFor(L, Z, n);
      var trial = null;
      if (matches(D.trial_once.if, ctx) && Z !== D.trial_once.size && inStock(L, S, D.trial_once.size, stock))
        trial = { size: D.trial_once.size, price: price(L, S, D.trial_once.size) };
      var fill = firstFill(L, S, a, stock);
      var fillAdded = !!(fill && o.fillAdded);
      var currentPlant = q3 === 'tofu' || q3 === 'cassava' ? q3 : null;
      var res = {
        cats_n: n, cats: a.cats, boxes: a.boxes, auto_box: autoBox, boxes_n: boxesN(a), q3: q3,
        rule: rule.id, recommended_litter: ruleLitter,
        litter: L, scent: S, size: Z, kg: D.sizes[Z].kg,
        name_key: 'name_' + L + '_' + S, size_key: 'size_' + Z,
        image: image(L, S, Z),
        one_time_price: price(L, S, Z), sub_price: subPrice(L, S, Z),
        freq: freq, lasts_weeks: Math.round(monthsOfUse(Z, n) * 52 / 12), per_cat_month: perCatMonth(L, S, Z, n),
        runs_out_day_upper: Math.floor(daysOfUse(Z, n, true)), early_days_upper: earlyDays(Z, n, freq),
        why: L === ruleLitter ? why : [],
        tips: L === ruleLitter ? tips : tips.filter(function (k) { return /lr_delay|auto_tested|boxes|tracking|5cats/.test(k); }),
        stock_note: stockNote, ideal: ideal,
        first_fill: fill, first_fill_added: fillAdded,
        first_order_total: r2(subPrice(L, S, Z) + (fillAdded ? fill.price : 0)),
        gradual: !currentPlant || currentPlant !== L,
        gradual_guarantee: q3 === 'clay_tried',
        trial: trial,
        other: other ? { size: other.size, size_key: 'size_' + other.size, freq: freqFor(other.litter, other.size, n),
                         per_cat_month: perCatMonth(other.litter, other.scent, other.size, n) } : null,
        using_alt: !!(o.useAlt && other),
        dust_claim: L === 'tofu' && why.indexOf('why_dust_human') < 0
      };
      res.pdp_sub = pdpUrl(L, S, Z, n, freq, fillAdded ? fill : null);
      res.pdp_once = pdpUrl(L, S, trial ? trial.size : Z, n, 'once', null);
      res.pdp_skip = res.pdp_sub;
      return res;
    }
    function stockFromParam(str, all) {
      var s = JSON.parse(JSON.stringify(D.in_stock));
      if (all) for (var L in s) for (var S in s[L]) for (var Z in s[L][S]) s[L][S][Z] = true;
      (str || '').split(',').forEach(function (p) { var q = p.trim().split('.'); if (q.length === 3 && s[q[0]] && s[q[0]][q[1]] && q[2] in s[q[0]][q[1]]) s[q[0]][q[1]][q[2]] = false; });
      return s;
    }

    /* ---- theme additions (PDP / cart): same frequency rule, but on any kg amount (qty > 1) ---- */
    // returns { key, short } — short = the box lasts less than min_days_to_subscribe at the working usage
    function freqForKg(L, Z, kg, n, tierKeys) {
      var nn = catsN(n), work = kg / D.monthly_kg[nn] * D.days_per_month, target = kg / D.monthly_kg_upper[nn] * D.days_per_month;
      var list = tierKeys ? D.tiers.filter(function (t) { return tierKeys.indexOf(t.key) >= 0; }) : tiersFor(L, Z);
      if (!list.length) return null;
      if (work < D.min_days_to_subscribe) return { key: list[0].key, short: true };
      var best = null;
      list.forEach(function (t) {
        if (t.days - target > D.max_early_days) return;
        var d = Math.abs(t.days - target);
        if (!best || d < best.d - 0.05 || (Math.abs(d - best.d) <= 0.05 && t.days < best.t.days)) best = { t: t, d: d };
      });
      return best ? { key: best.t.key, short: false } : { key: list[0].key, short: true };
    }

    return { recommend: recommend, stockFromParam: stockFromParam, freqFor: freqFor, freqForKg: freqForKg, tiersFor: tiersFor,
             daysOfUse: daysOfUse, monthsOfUse: monthsOfUse, earlyDays: earlyDays, q3Of: q3Of, catsN: catsN, boxesN: boxesN,
             subPrice: subPrice, price: price, inStock: inStock,
             setWeekly: setWeekly, hasWeekly: function () { return HAS_WEEKLY_PLANS; }, D: D };
  };

  /* ---------------------------------------------------------------
     2) CATALOG ADAPTER — window.NL5_PRODUCTS (snippets/nl5-product-data.liquid) → decision table
     --------------------------------------------------------------- */
  NL5.SIZE_KEYS = ['2pk', '6bag', '12kg'];   // size-cards buy box (A) — unchanged
  // B buy box (Jing 10/9, bags-by-cats): every bag count is its own variant (1 / 2 / 3 / 6 bags) + the 12 kg bag
  NL5.ALL_SIZE_KEYS = ['1pk', '2pk', '3pk', '4pk', '5pk', '6bag', '12kg'];   // 4 / 5 bags listed since Jing 10/12
  // B bag stepper (Jing 10/12): n bags (1–12) → variant lines. n ≤ 6 = that bag count's own variant; 7–12 = the 6-bag case × a + one
  // r-bag variant (n = 6a + r, r = 0 → no second line). 6 bags always ship as the factory case (DH: case $1 vs single bags $1 each + packing).
  NL5.BAG_KEY = { 1: '1pk', 2: '2pk', 3: '3pk', 4: '4pk', 5: '5pk', 6: '6bag' };
  NL5.BAG_N = { '1pk': 1, '2pk': 2, '3pk': 3, '4pk': 4, '5pk': 5, '6bag': 6 };
  NL5.bagsSplit = function (n) {
    n = Math.max(1, Math.min(12, parseInt(n, 10) || 1));
    var a = Math.floor(n / 6), r = n % 6, out = [];
    if (a) out.push({ size: '6bag', qty: a });
    if (r) out.push({ size: NL5.BAG_KEY[r], qty: 1 });
    return out;
  };
  NL5.bagsSplitLabel = function (n) { return NL5.bagsSplit(n).map(function (l) { return NL5.BAG_N[l.size] + '×' + l.qty; }).join('+'); };   // "6×1+2×1" (line property _bags_split)
  // merge the B sizes (kg) and their no-plan tier table from D.bags into D (idempotent; the A sizes are never touched)
  NL5.prepBags = function (D) {
    var B = D && D.bags; if (!B || D.__bags) return D; D.__bags = true;
    Object.keys(B.sizes || {}).forEach(function (z) { if (!D.sizes[z]) D.sizes[z] = { kg: B.sizes[z].kg }; });
    Object.keys(B.tier_availability || {}).forEach(function (L) {
      D.tier_availability[L] = D.tier_availability[L] || {};
      Object.keys(B.tier_availability[L]).forEach(function (z) { if (!D.tier_availability[L][z]) D.tier_availability[L][z] = B.tier_availability[L][z].slice(); });
    });
    return D;
  };
  // selling plan → tier key by parsing "every N week(s)/month(s)/day(s)" (or "monthly", "bi-weekly")
  // Selling plan → tier key.  (C1, audit 10/7) Supr encodes the schedule in the plan option value, e.g.
  //   "B-MONTH_2,D-MONTH_2,N-Delivery Every Two Months"  (B = billing, D = delivery, N = label)
  // Read D-<UNIT>_<n> first. Billing ≠ delivery (e.g. "B-MONTH_4,D-MONTH_1" = prepaid 4 boxes) → not a plain
  // subscription tier → null (ignored). Without a code, parse only the delivery label (the N- part, or the text after
  // the last comma of the name) so a group name like "10% OFF Monthly Supply Plan" can never decide the tier.
  var NUMW = { one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7, eight: 8, nine: 9, ten: 10, eleven: 11, twelve: 12 };
  NL5.planDays = function (D, plan) {
    var opt = String((plan && plan.option) || ''), name = String((plan && plan.name) || '');
    var unitDays = { DAY: 1, WEEK: 7, MONTH: D.days_per_month, YEAR: 365 };
    var d = opt.match(/(?:^|,)\s*D-(DAY|WEEK|MONTH|YEAR)_(\d+)/i), b = opt.match(/(?:^|,)\s*B-(DAY|WEEK|MONTH|YEAR)_(\d+)/i);
    if (d) {
      if (b && (b[1].toUpperCase() !== d[1].toUpperCase() || b[2] !== d[2])) return null;   // prepaid / billing ≠ delivery
      return unitDays[d[1].toUpperCase()] * parseInt(d[2], 10);
    }
    var label = (opt.match(/(?:^|,)\s*N-(.+)$/i) || [])[1] || (/,/.test(name) ? name.slice(name.lastIndexOf(',') + 1) : (opt || name));
    var t = String(label).toLowerCase().replace(/\b(one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve)\b/g, function (w) { return String(NUMW[w]); });
    if (/prepaid|pay once/.test(t)) return null;
    var m = t.match(/(\d+)\s*-?\s*(days?|weeks?|wks?|months?|mos?)\b/);
    if (m) { var k = parseInt(m[1], 10); return /^d/.test(m[2]) ? k : /^w/.test(m[2]) ? k * 7 : k * D.days_per_month; }
    if (/bi-?weekly|every other week|fortnight/.test(t)) return 14;
    if (/every week|^\s*weekly/.test(t)) return 7;
    if (/quarterly/.test(t)) return 3 * D.days_per_month;
    if (/every month|^\s*monthly\s*$|delivered monthly/.test(t)) return D.days_per_month;
    return null;
  };
  NL5.tierOfPlan = function (D, plan) {
    var days = NL5.planDays(D, plan);
    if (days == null) return null;
    var best = null;
    D.tiers.forEach(function (t) { var d = Math.abs(t.days - days); if (!best || d < best.d) best = { key: t.key, d: d }; });
    return best && best.d <= 2.5 ? best.key : null;   // 4w (28) vs monthly (30.4) stay distinct; D-WEEK_6 / "every 6 weeks" → 6w (42, 10/12); every 4 months → no tier (ignored)
  };
  // variant meta from Liquid: { id, sku, size, scent, price (cents), available, plans:[{id,name,option,price}] }
  NL5.sizeOf = function (v) {
    // 10/12: the SKU decides first — "5 bags · 12 kg" (NL-xx-5PK) must never read as the 12 kg bag
    var sku = String(v.sku || '').toUpperCase(), m = /-(\d)PK\b/.exec(sku);
    if (m && NL5.BAG_KEY[m[1]]) return NL5.BAG_KEY[m[1]];
    if (/-12KG\b/.test(sku)) return '12kg';
    var s = (sku + ' ' + (v.size_label || '') + ' ' + (v.title || '')).toUpperCase();
    var b = /(^|[^\d.])([1-6])\s*-?\s*(PK|PACKS?|BAGS?)\b/.exec(s);   // "4 bags · 9.6 kg", "6 packs" (never the "12 kg bag": 12 is followed by kg)
    if (b) return NL5.BAG_KEY[b[2]];
    if (/12\s*KG/.test(s)) return '12kg';
    return null;
  };
  NL5.scentOf = function (litter, v) {
    if (litter === 'cassava') return 'unscented';
    var s = ((v.sku || '') + ' ' + (v.scent_label || '') + ' ' + (v.title || '')).toUpperCase();
    if (/GREEN|-GT-/.test(s)) return 'green-tea';
    if (/WHITE|VANILLA|-WT-/.test(s)) return 'white-tea';
    return null;
  };
  /**
   * C2 (audit 10/7): NL5 reads the store's existing products. window.NL5_CATALOG (snippets/nl5-catalog.liquid) =
   *   { map: [{litter, scent, size, handle, sku}], products: { handle: <nl5-product-data> } }
   * → { tofu: {litter, url, scentUrl:{scent:url}, handles, variants:[…]}, cassava: {…} } — the shape applyCatalog expects.
   * Variants are matched by handle + SKU (case-insensitive); anything not in the map (fig 12 kg) is dropped.
   */
  NL5.buildProducts = function (cat) {
    var out = {}; if (!cat || !cat.map) return out;
    var idx = {};
    Object.keys(cat.products || {}).forEach(function (h) {
      var p = cat.products[h]; if (!p) return;
      (p.variants || []).forEach(function (v) { if (v.sku) idx[h + '|' + String(v.sku).toUpperCase()] = { v: v, p: p }; });
    });
    cat.map.forEach(function (m) {
      var hit = idx[m.handle + '|' + String(m.sku).toUpperCase()]; if (!hit) return;
      var L = m.litter, P = out[L] = out[L] || { litter: L, url: null, scentUrl: {}, handles: [], variants: [] };
      var v = {}; for (var k in hit.v) v[k] = hit.v[k];
      v.size = m.size; v.scent = m.scent; v.litter = L; v.handle = m.handle; v.product_id = hit.p.id; v.product_url = hit.p.url; v.product_title = hit.p.title;
      P.variants.push(v);
      if (P.handles.indexOf(m.handle) < 0) P.handles.push(m.handle);
      if (m.size !== '12kg' && !P.scentUrl[m.scent]) P.scentUrl[m.scent] = hit.p.url;   // the scent's own product page (not the bulk bag)
      if (!P.url && m.size !== '12kg') P.url = hit.p.url;
    });
    Object.keys(out).forEach(function (L) { if (!out[L].url && out[L].variants[0]) out[L].url = out[L].variants[0].product_url; });
    return out;
  };
  /* ---- B buy box (Jing 10/9, bags-by-cats): one recommendation table for the PDP and the quiz result (D.bags.reco) ---- */
  function catsNum(c) { return c === '4+' || c === 4 || c === '4' ? 4 : Math.max(1, Math.min(4, parseInt(c, 10) || 2)); }
  NL5.tierDays = function (D, key) { for (var i = 0; i < D.tiers.length; i++) if (D.tiers[i].key === key) return D.tiers[i].days; return null; };
  /**
   * a: { inStock(z) → this litter / scent has the size and it is in stock, tierKeys(z) → available delivery tiers (D.tiers order),
   *      autoFreq(z) → tier from the usual kg rule (freqForKg) or null }
   * → { cats, size, freq, wanted: {size, freq}, exact, fallback, saver: {size, freq} | null } or null (nothing in stock)
   * freq = the table's frequency when the size has it, else the nearest available tier by days (ties → shorter); hidden
   * (over-60-day) tiers are never available, so they are never picked. The saver option only with its exact frequency.
   */
  NL5.bagsPlan = function (D, cats, a) {
    var B = D.bags || {}, n = catsNum(cats), row = (B.reco || {})[String(n)] || {};
    function nearest(z, want) {
      var ks = a.tierKeys(z); if (!ks.length) return null;
      if (want && ks.indexOf(want) >= 0) return want;
      var wd = NL5.tierDays(D, want); if (wd == null) return a.autoFreq(z) || ks[0];
      var best = null;
      ks.forEach(function (k) { var d = Math.abs(NL5.tierDays(D, k) - wd); if (!best || d < best.d - 0.01 || (Math.abs(d - best.d) <= 0.01 && NL5.tierDays(D, k) < NL5.tierDays(D, best.k))) best = { k: k, d: d }; });
      return best.k;
    }
    var size = null, fb = false;
    if (row.size && a.inStock(row.size)) size = row.size;
    else { ((B.fallback || {})[String(n)] || []).some(function (z) { if (a.inStock(z)) { size = z; return true; } return false; }); fb = !!size; }
    if (!size) return null;
    var freq = fb ? (a.autoFreq(size) || a.tierKeys(size)[0] || null) : nearest(size, row.freq);
    var saver = null, sv = row.saver;
    if (!fb && sv && a.inStock(sv.size) && a.tierKeys(sv.size).indexOf(sv.freq) >= 0) saver = { size: sv.size, freq: sv.freq };
    return { cats: n, size: size, freq: freq, wanted: { size: row.size, freq: row.freq }, exact: !fb && freq === row.freq, fallback: fb, saver: saver };
  };
  // how long an amount lasts: [days at the upper usage (rounded down), days at the working usage (rounded down)]
  NL5.bagsLasts = function (D, kg, cats) {
    var n = catsNum(cats);
    return [Math.floor(kg / D.monthly_kg_upper[n] * D.days_per_month), Math.floor(kg / D.monthly_kg[n] * D.days_per_month)];
  };
  // per cat per month (cents): price per delivery × days_per_month ÷ delivery days ÷ cats; no delivery tier (one-time / 1 bag) →
  // the days the amount lasts at the working usage
  NL5.bagsPcmCents = function (D, cents, kg, cats, freq) {
    var n = catsNum(cats), days = (freq && NL5.tierDays(D, freq)) || kg / D.monthly_kg[n] * D.days_per_month;
    return cents * D.days_per_month / days / n;
  };
  NL5.litterOfVariant = function (products, id) {
    for (var L in products) for (var i = 0; i < products[L].variants.length; i++) if (String(products[L].variants[i].id) === String(id)) return L;
    return null;
  };

  /**
   * products: window.NL5_PRODUCTS = { tofu: {...}, cassava: {...} } (either may be missing)
   * opts: { forceOOS: 'tofu.green-tea.6bag,...', hasWeekly: bool }
   * Mutates D (prices_cad / sub_prices_cad / in_stock / tier_availability) and returns
   * { variant(L,S,Z), plans(L,S,Z), hasPlans, product(L) }.
   */
  NL5.applyCatalog = function (D, products, opts) {
    opts = opts || {};
    NL5.prepBags(D);
    var index = {}, anyPlans = false, tierAvail = {}, oneTimeOnly = (D.bags && D.bags.one_time_only) || [];
    ['tofu', 'cassava'].forEach(function (L) {
      var p = products && products[L];
      if (!p) return;
      (p.variants || []).forEach(function (v) {
        var Z = v.size || NL5.sizeOf(v), S = v.scent || NL5.scentOf(L, v);
        if (!Z || !S || !D.prices_cad[L] || !D.prices_cad[L][S]) return;
        v.size = Z; v.scent = S; v.litter = L;
        index[L + '|' + S + '|' + Z] = v;
        D.prices_cad[L][S][Z] = v.price / 100;
        D.in_stock[L][S][Z] = !!v.available;
        // 1 bag: one-time only on its own (tier null); tier_raw keeps the plan's tier for the 1-bag part of a 7-bag order (10/12)
        (v.plans || []).forEach(function (pl) { var t = pl.tier_raw || pl.tier || NL5.tierOfPlan(D, pl); pl.tier_raw = t; pl.tier = oneTimeOnly.indexOf(Z) >= 0 ? null : t; });
        var plans = (v.plans || []).filter(function (pl) { return pl.tier; });
        if (plans.length) {
          anyPlans = true;
          tierAvail[L] = tierAvail[L] || {};
          var keys = (tierAvail[L][Z] || []).slice();
          plans.forEach(function (pl) { if (keys.indexOf(pl.tier) < 0) keys.push(pl.tier); });
          tierAvail[L][Z] = keys;
          // subscription price shown = the variant's lowest plan price (Supr: same % on every tier)
          var min = Math.min.apply(null, plans.map(function (pl) { return pl.price; }));
          D.sub_prices_cad = D.sub_prices_cad || {};
          D.sub_prices_cad[L] = D.sub_prices_cad[L] || {};
          D.sub_prices_cad[L][S] = D.sub_prices_cad[L][S] || {};
          D.sub_prices_cad[L][S][Z] = min / 100;
        }
      });
    });
    if (anyPlans) {
      // real plans replace the table; sizes without plans are not subscribable
      ['tofu', 'cassava'].forEach(function (L) { NL5.ALL_SIZE_KEYS.forEach(function (Z) { D.tier_availability[L] = D.tier_availability[L] || {}; D.tier_availability[L][Z] = (tierAvail[L] && tierAvail[L][Z]) || []; }); });
      D.weekly_tiers_added_everywhere = [];
    } else if (typeof opts.hasWeekly === 'boolean') {
      D.has_weekly_plans = opts.hasWeekly;
    }
    // 10/9 (拍板单 #13 default): hide delivery tiers longer than N days (60 → drops "every 2 months" 60.8 d and "every 3 months").
    // Plans are tiered against the FULL tier list first, then over-limit plans lose their tier (never re-mapped to a shorter one).
    // Source: opts.maxTierDays, else window.NL5_TIER_MAX_DAYS (set by the NL5 sections' hide_over_60_day_tiers; 0 = keep all).
    var maxD = opts.maxTierDays != null ? Number(opts.maxTierDays) : Number((typeof root !== 'undefined' && root.NL5_TIER_MAX_DAYS) || 0);
    if (maxD > 0) {
      var over = D.tiers.filter(function (t) { return t.days > maxD; }).map(function (t) { return t.key; });
      if (over.length) {
        Object.keys(index).forEach(function (k) { (index[k].plans || []).forEach(function (pl) { if (over.indexOf(pl.tier) >= 0) pl.tier = null; if (over.indexOf(pl.tier_raw) >= 0) pl.tier_raw = null; }); });
        Object.keys(D.tier_availability || {}).forEach(function (L) { Object.keys(D.tier_availability[L] || {}).forEach(function (Z) {
          D.tier_availability[L][Z] = (D.tier_availability[L][Z] || []).filter(function (k) { return over.indexOf(k) < 0; }); }); });
        D.weekly_tiers_added_everywhere = (D.weekly_tiers_added_everywhere || []).filter(function (k) { return over.indexOf(k) < 0; });
        D.tiers = D.tiers.filter(function (t) { return over.indexOf(t.key) < 0; });
        D.hidden_tiers = over;
      }
    }
    (opts.forceOOS || '').split(',').forEach(function (p) {
      var q = p.trim().split('.');
      if (q.length === 3 && D.in_stock[q[0]] && D.in_stock[q[0]][q[1]] && q[2] in D.in_stock[q[0]][q[1]]) D.in_stock[q[0]][q[1]][q[2]] = false;
    });
    return {
      hasPlans: anyPlans,
      variant: function (L, S, Z) { return index[L + '|' + S + '|' + Z] || null; },
      plans: function (L, S, Z) { var v = index[L + '|' + S + '|' + Z]; return v ? (v.plans || []).filter(function (pl) { return pl.tier; }) : []; },
      planFor: function (L, S, Z, tierKey) {   // same tier in two Supr groups (white/green 6-pack) → cheaper plan, then first
        var v = index[L + '|' + S + '|' + Z]; if (!v) return null;
        return (v.plans || []).filter(function (pl) { return pl.tier === tierKey; }).sort(function (a, b) { return a.price - b.price; })[0] || null; },
      // 10/12: the r-bag part of a 7–12 bag order — plans by tier_raw (the 1-bag variant's plans count here, never on their own)
      plansSplit: function (L, S, Z) { var v = index[L + '|' + S + '|' + Z]; return v ? (v.plans || []).filter(function (pl) { return pl.tier_raw; }) : []; },
      planForSplit: function (L, S, Z, tierKey) {
        var v = index[L + '|' + S + '|' + Z]; if (!v) return null;
        return (v.plans || []).filter(function (pl) { return pl.tier_raw === tierKey; }).sort(function (a, b) { return a.price - b.price; })[0] || null; },
      product: function (L) { return products && products[L]; }
    };
  };

  /* ---------------------------------------------------------------
     3) BROWSER HELPERS
     --------------------------------------------------------------- */
  NL5.decode = function (s) {
    if (typeof document === 'undefined' || s == null) return s;
    if (!/[&<]/.test(s)) return s;
    var t = document.createElement('textarea'); t.innerHTML = s; return t.value;
  };
  // dict values come from {{ 'nl5.x' | t | json }} (HTML-escaped by Shopify) → decode once
  NL5.makeT = function (dict) {
    var cache = {};
    return function (key, vars) {
      var s = cache[key];
      if (s === undefined) { s = dict && dict[key] != null ? NL5.decode(String(dict[key])) : null; cache[key] = s; }
      if (s == null) return '[' + key + ']';
      if (vars) s = s.replace(/\{(\w+)\}/g, function (m, k) { return (k in vars) ? vars[k] : m; });
      return s;
    };
  };
  NL5.esc = function (s) { return String(s == null ? '' : s).replace(/[&<>"]/g, function (c) { return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]; }); };
  // Shopify money format ({{ shop.money_format | json }} → window.NL5_SHOP.money_format)
  NL5.formatMoney = function (cents, format) {
    format = format || (root.NL5_SHOP && root.NL5_SHOP.money_format) || '${{amount}}';
    cents = Math.round(Number(cents) || 0);
    function fmt(n, dec, th, ds) {
      th = th == null ? ',' : th; ds = ds == null ? '.' : ds;
      var str = (n / 100).toFixed(dec), parts = str.split('.');
      parts[0] = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + th);
      return parts[0] + (parts[1] ? ds + parts[1] : '');
    }
    return String(format).replace(/\{\{\s*(\w+)\s*\}\}/, function (m, k) {
      switch (k) {
        case 'amount_no_decimals': return fmt(cents, 0);
        case 'amount_with_comma_separator': return fmt(cents, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return fmt(cents, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return fmt(cents, 2, "'", '.');
        default: return fmt(cents, 2);
      }
    }).replace(/<[^>]*>/g, '');
  };
  NL5.money = function (dollars) { return NL5.formatMoney(Math.round(dollars * 100)); };

  NL5.ls = {
    get: function (k) { try { return root.localStorage.getItem(k); } catch (e) { return null; } },
    set: function (k, v) { try { root.localStorage.setItem(k, v); } catch (e) {} },
    del: function (k) { try { root.localStorage.removeItem(k); } catch (e) {} },
    json: function (k) { try { return JSON.parse(root.localStorage.getItem(k) || 'null'); } catch (e) { return null; } }
  };

  // C3 (audit 10/7): there is no GTM on the site, so dataLayer alone reaches nothing. Every event still goes to dataLayer,
  // and is also sent with gtag('event', …) (the theme loads AW-11054039346; GA4 comes via the Google & YouTube channel)
  // and with Shopify.analytics.publish (custom pixels / customer events). Old live-quiz names are kept for gtag:
  // quiz_step_N → quiz_step {step: N}; quiz_gate_submit → also generate_lead + quiz_lead.
  // Only quiz_* (+ the two lead events) go to gtag; PDP/cart add_to_cart is already a standard Shopify event (no double count).
  function flat(o) {   // gtag / publish get flat params (no nested objects such as cart_attributes, no PII is ever tracked)
    var r = {};
    for (var k in o) { var v = o[k]; if (k === 'event' || k === 'cart_attributes') continue;
      if (v == null || typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') r[k] = v;
      else if (typeof v === 'object' && !Array.isArray(v) && v.size) r[k] = v.size; }
    return r;
  }
  // K3 / K5 / K14 (分流方案 3.4): split group. snippets/nl5-exp.liquid sets window.NL5_EXP = {group, name} in <head>
  // (nl5 in the new theme, control in the old one). Without it: html.nl5-on → nl5, else control (same as the quiz's C6 rule).
  NL5.expGroup = function () {
    var e = root.NL5_EXP;
    if (e && e.group) return String(e.group);
    return typeof document !== 'undefined' && document.documentElement.classList.contains('nl5-on') ? 'nl5' : 'control';
  };
  NL5.expName = function () { var e = root.NL5_EXP; return (e && e.name) || 'nl5-2026q4'; };
  NL5.gtagName = function (event) { var m = /^quiz_step_(\d)$/.exec(event); return m ? { name: 'quiz_step', step: Number(m[1]) } : { name: event }; };
  NL5.track = function (event, data) {
    root.dataLayer = root.dataLayer || [];
    var payload = { event: event };
    for (var k in (data || {})) payload[k] = data[k];
    payload.exp_group = NL5.expGroup(); payload.exp_name = NL5.expName();   // K5: every NL5 event carries the split group
    root.dataLayer.push(payload);
    var sent = [];
    try {
      var p = flat(payload); p.nl5 = true;
      if (/^quiz_/.test(event) && typeof root.gtag === 'function') {
        var g = NL5.gtagName(event), gp = {}; for (var a in p) gp[a] = p[a]; if (g.step) gp.step = g.step;
        root.gtag('event', g.name, gp); sent.push('gtag:' + g.name);
        if (event === 'quiz_gate_submit') {
          root.gtag('event', 'generate_lead', { method: 'nl5_quiz', marketing_consent: !!p.marketing_consent, nl5: true });
          root.gtag('event', 'quiz_lead', { marketing_consent: !!p.marketing_consent, phone_given: !!p.phone_given, nl5: true });
          sent.push('gtag:generate_lead', 'gtag:quiz_lead');
        }
      }
      if (root.Shopify && root.Shopify.analytics && typeof root.Shopify.analytics.publish === 'function') {
        root.Shopify.analytics.publish(event, p); sent.push('publish:' + event);
      }
    } catch (e) {}
    NL5.lastSent = sent;
    if (NL5.debug) try { console.log('[nl5]', event, payload, sent); } catch (e) {}
  };
  NL5.debug = typeof location !== 'undefined' && /[?&]nl5debug=1/.test(location.search);

  NL5.loadJSON = function (url) {
    NL5._json = NL5._json || {};
    if (!NL5._json[url]) NL5._json[url] = fetch(url, { credentials: 'same-origin' }).then(function (r) { if (!r.ok) throw new Error(r.status); return r.json(); });
    return NL5._json[url];
  };
  NL5.loadDecision = function () {
    var url = root.NL5_ASSETS && root.NL5_ASSETS.decision;
    if (!url) return Promise.reject(new Error('NL5_ASSETS.decision missing'));
    // fresh copy each time (applyCatalog mutates it)
    return NL5.loadJSON(url).then(function (d) { return JSON.parse(JSON.stringify(d)); });
  };

  /* ---- cart AJAX (Shopify Ajax API) ---- */
  function root_() { return (root.Shopify && root.Shopify.routes && root.Shopify.routes.root) || '/'; }
  function postJSON(path, body) {
    return fetch(root_() + path, { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) })
      .then(function (r) { return r.json().then(function (j) { if (!r.ok) { var e = new Error(j.description || j.message || r.status); e.data = j; throw e; } return j; }); });
  }
  NL5.cartGet = function () { return fetch(root_() + 'cart.js', { credentials: 'same-origin' }).then(function (r) { return r.json(); }); };
  // K14: every NL5 add carries the hidden line property _nl_variant (orders placed without cart attributes still show the group)
  NL5.cartAdd = function (items) {
    var g = NL5.expGroup();
    (items || []).forEach(function (it) { it.properties = it.properties || {}; if (!it.properties._nl_variant) it.properties._nl_variant = g; });
    return postJSON('cart/add.js', { items: items });
  };
  NL5.cartChange = function (body) { return postJSON('cart/change.js', body); };
  NL5.cartUpdateAttributes = function (attrs) { return postJSON('cart/update.js', { attributes: attrs }).catch(function () {}); };

  // Minimog drawer: re-render via its own section fetch, then open. Falls back to /cart.
  NL5.refreshCartDrawer = function () {
    var el = typeof document !== 'undefined' && document.getElementById('MinimogCartDrawer');
    try {
      if (el && typeof el.onCartDrawerUpdate === 'function') return Promise.resolve(el.onCartDrawerUpdate());
      if (root.MinimogCartDrawer && typeof root.MinimogCartDrawer.onCartDrawerUpdate === 'function') return Promise.resolve(root.MinimogCartDrawer.onCartDrawerUpdate());
    } catch (e) {}
    return Promise.resolve(false);
  };
  NL5.openCartDrawer = function () {
    var el = document.getElementById('MinimogCartDrawer');
    if (!el) { location.href = root_() + 'cart'; return Promise.resolve(); }
    return NL5.refreshCartDrawer().then(function () {
      try {
        if (typeof el.openDrawer === 'function') return el.openDrawer();
        if (typeof el.open === 'function') return el.open();
        if (typeof el.show === 'function') return el.show();
      } catch (e) {}
      // fallback: click the header cart toggle Minimog binds to the drawer
      var t = document.querySelector('[data-cart-drawer-toggle], .m-cart-icon-bubble, a[href$="/cart"].m-header__cart, header a[href$="/cart"]');
      if (t) t.click(); else location.href = root_() + 'cart';
    });
  };

  /* ---- bottom offset: the theme's region suggestion bar #nl-geo-suggest (snippets/nl-geo-suggest, position fixed,
     bottom 0, z-index 9998, ~110 px, shown to visitors outside the store's market) would cover the quiz bar / tag and the
     PDP sticky bar. We publish its visible height as --nl5-bottom-offset on <html>; nl5-quiz.css / nl5-pdp.css lift by it. ---- */
  NL5.watchBottomBar = function () {
    if (NL5.__bottomWatch || typeof document === 'undefined') return; NL5.__bottomWatch = true;
    var ro = null, seen = null;
    function measure() {
      var g = document.getElementById('nl-geo-suggest'), h = 0;
      if (g) {
        var cs = getComputedStyle(g), r = g.getBoundingClientRect();
        if (cs.display !== 'none' && cs.visibility !== 'hidden' && r.height > 0 && r.bottom >= innerHeight - 4 && r.top < innerHeight) h = Math.ceil(innerHeight - r.top);
        if (g !== seen && window.ResizeObserver) { seen = g; if (ro) ro.disconnect(); ro = new ResizeObserver(measure); ro.observe(g); }
      }
      document.documentElement.style.setProperty('--nl5-bottom-offset', h + 'px');
    }
    measure();
    addEventListener('resize', measure, { passive: true });
    new MutationObserver(function () { clearTimeout(measure._t); measure._t = setTimeout(measure, 50); }).observe(document.body, { childList: true, subtree: false, attributes: true, attributeFilter: ['class', 'style'] });
    var g0 = document.getElementById('nl-geo-suggest'); if (g0) new MutationObserver(measure).observe(g0, { attributes: true, attributeFilter: ['class', 'style', 'hidden'] });
    var n = 0, iv = setInterval(function () { measure(); if (++n > 20) clearInterval(iv); }, 500);   // the bar is injected / animated in after load
  };

  /* ---- first-order offer (Jing 10/8 final): leave an email AND a mobile number → two codes on the spot: onetime_code (10% off a one-time first
     order) and sub_code (15% off the first subscription box, counted on the one-time price: first box = price × 0.85, not on
     top of the subscription price). 6-Bag / 12 kg subscriptions also get a free catnip lollipop (store automatic BXGY, not a code).
     The codes / BXGY themselves are Shopify discounts on the store; the theme only shows them and sends selling_plan.
     offer_end_date (YYYY-MM-DD, end of that day on the visitor's clock): empty = offer on, no "Time-limited" wording;
     past = offer lines and codes gone. Pure (node-testable). ---- */
  NL5.offerState = function (cfg, lang, now) {
    cfg = cfg || {}; now = now || new Date();
    var raw = String(cfg.offer_end_date || '').trim(), m = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(raw);
    var end = m ? new Date(+m[1], +m[2] - 1, +m[3], 23, 59, 59, 999) : null;
    if (end && isNaN(end.getTime())) end = null;
    var on = !end || now.getTime() <= end.getTime();
    var endText = '';
    if (end) try { endText = end.toLocaleDateString(/^fr/i.test(lang || '') ? 'fr-CA' : 'en-CA', { month: 'short', day: 'numeric' }); } catch (e) { endText = raw; }
    var onceCode = on ? String(cfg.onetime_code || '').trim() : '', subCode = on ? String(cfg.sub_code || '').trim() : '';
    var oncePct = Number(cfg.onetime_pct) || 10, subPct = Number(cfg.sub_pct) || 15;
    // ladder incl. the B bag counts (10/9): '2pk' = every subscribable size (2 / 3 / 6 bags, 12 kg), '6bag' = 6 bags + 12 kg
    var sizes = NL5.ALL_SIZE_KEYS, minI = sizes.indexOf(cfg.lollipop_min_size); if (minI < 0) minI = sizes.indexOf('6bag');   // default 6bag
    var gift = !!(on && cfg.lollipop_enabled);
    // lollipop_scope (Jing 10/9): 'quiz_lead' (default) = first order of anyone who finished the quiz and left email + mobile —
    // any size (1 bag included), subscription or one-time; 'sub_min_size' = the 10/8 rule (subscription first box, size ≥ min)
    var scope = cfg.lollipop_scope === 'sub_min_size' ? 'sub_min_size' : 'quiz_lead';
    return {
      on: on, limited: !!end && on, endText: endText, badDate: !!raw && !m,
      onceCode: onceCode, subCode: subCode, oncePct: oncePct, subPct: subPct,
      hasCodes: !!(onceCode || subCode), maxPct: Math.max(onceCode ? oncePct : 0, subCode ? subPct : 0),
      gift: gift, giftMin: sizes[minI], scope: scope,   // giftMin (sub_min_size only): '2pk' = all sizes, '6bag' = 6 bags & 12 kg, '12kg' = 12 kg only
      // sub_min_size: callers ask only for subscription lines. quiz_lead: ctx.lead = quiz finished + email & mobile (NL5.quizLead)
      giftFor: function (size, ctx) {
        if (scope === 'quiz_lead') return gift && sizes.indexOf(size) >= 0 && !!(ctx && ctx.lead);
        return gift && size !== '1pk' && sizes.indexOf(size) >= minI;
      },
      // codes for a saved sign-up ({at, phone, …} from localStorage) — null without email + phone, or when the offer is off
      codes: function (saved) {
        if (!saved || !saved.phone || !(onceCode || subCode)) return null;   // codes need email + mobile number
        return { once: onceCode ? { code: onceCode, pct: oncePct } : null, sub: subCode ? { code: subCode, pct: subPct } : null };
      },
      subFirstBoxCents: function (onceCents) { return Math.round(onceCents * (1 - subPct / 100)); },   // one-time price × 0.85
      onceFirstCents: function (onceCents) { return Math.round(onceCents * (1 - oncePct / 100)); }
    };
  };

  // 10-digit North American mobile → E.164 (+1XXXXXXXXXX) or null (a leading 1 is accepted)
  NL5.normPhone = function (s) {
    var d = String(s || '').replace(/\D/g, '');
    if (d.length === 11 && d.charAt(0) === '1') d = d.slice(1);
    return /^[2-9]\d{2}[2-9]\d{6}$/.test(d) ? '+1' + d : null;
  };

  // phone field (Jing 10/8): digits only, leading 1 dropped, max 10, shown as (647) 570-8795
  NL5.phoneDigits = function (s) { return String(s || '').replace(/\D/g, '').replace(/^1+/, '').slice(0, 10); };
  NL5.formatPhone = function (d) {
    d = NL5.phoneDigits(d);
    if (!d) return '';
    if (d.length <= 3) return '(' + d;
    if (d.length <= 6) return '(' + d.slice(0, 3) + ') ' + d.slice(3);
    return '(' + d.slice(0, 3) + ') ' + d.slice(3, 6) + '-' + d.slice(6);
  };
  // live-format an <input type="tel">; onChange(complete) after every edit; errEl gets msg on blur while 1–9 digits are typed
  NL5.bindPhone = function (input, errEl, msg, onChange) {
    if (!input || input.__nl5phone) return; input.__nl5phone = true;
    function apply() {
      var d = NL5.phoneDigits(input.value), f = NL5.formatPhone(d);
      if (input.value !== f) { input.value = f; try { input.setSelectionRange(f.length, f.length); } catch (e) {} }
      if (d.length === 10 && errEl) errEl.textContent = '';
      input.setAttribute('aria-invalid', d.length === 10 || !d.length ? 'false' : 'true');
      if (onChange) onChange(d.length === 10);
    }
    input.addEventListener('input', apply);
    input.addEventListener('paste', function () { setTimeout(apply, 0); });
    input.addEventListener('blur', function () { var n = NL5.phoneDigits(input.value).length; if (errEl) errEl.textContent = n && n < 10 ? msg : ''; });
    apply();
  };

  /* ---- contact capture (Jing 10/8 final): email + mobile number are both required for the codes; each has its own consent box
     (never pre-ticked). Codes are shown either way; an unticked channel is stored, never messaged:
     - Shopify customer form: email only, and only with the email box ticked (it sets accepts_marketing)
     - Klaviyo Client API: 1) POST /client/profiles — stores email + phone_number (E.164) + properties, no consent;
       2) POST /client/subscriptions — only the ticked channels (email ← email box, SMS ← SMS box), into the list ---- */
  NL5.klaviyoProfileBody = function (o) {
    var props = {}; for (var pk in (o.properties || {})) props[pk] = o.properties[pk];
    props['NL exp group'] = NL5.expGroup();   // K12: lead-rate guardrail numerator by group
    var attrs = { properties: props };
    if (o.email) attrs.email = o.email;
    if (o.phone) attrs.phone_number = o.phone;
    return { data: { type: 'profile', attributes: attrs } };
  };
  NL5.klaviyoSubscribeBody = function (o) {
    var attrs = {}, subs = {};
    if (o.consent && o.email) { attrs.email = o.email; subs.email = { marketing: { consent: 'SUBSCRIBED' } }; }
    if (o.smsConsent && o.phone) { attrs.phone_number = o.phone; subs.sms = { marketing: { consent: 'SUBSCRIBED' } }; }
    if (!subs.email && !subs.sms) return null;
    attrs.subscriptions = subs;
    return { data: { type: 'subscription', attributes: { custom_source: o.source || 'Litter Quiz', profile: { data: { type: 'profile', attributes: attrs } } },
      relationships: { list: { data: { type: 'list', id: o.klaviyoList } } } } };
  };
  // form: a rendered Shopify customer form element (hidden) — we fill contact[email]/contact[tags] and POST it with fetch
  NL5.submitEmail = function (o) {
    var jobs = [];
    if (o.form && o.consent && o.email) {
      var fd = new FormData(o.form);
      fd.set('contact[email]', o.email);
      fd.set('contact[tags]', o.tags || 'newsletter');
      fd.set('contact[accepts_marketing]', 'true');
      jobs.push(fetch(o.form.getAttribute('action') || '/contact', { method: 'POST', body: fd, credentials: 'same-origin', redirect: 'follow' })
        .then(function (r) { return { shopify: r.ok, challenged: /challenge/.test(r.url || '') }; }).catch(function () { return { shopify: false }; }));
    }
    if (o.klaviyoKey) {
      var base = 'https://a.klaviyo.com/client/', q = '?company_id=' + encodeURIComponent(o.klaviyoKey), H = { 'Content-Type': 'application/json', revision: '2024-10-15' };
      var sub = o.klaviyoList ? NL5.klaviyoSubscribeBody(o) : null;
      jobs.push(fetch(base + 'profiles/' + q, { method: 'POST', headers: H, body: JSON.stringify(NL5.klaviyoProfileBody(o)) })
        .then(function (r) { return { klaviyo_profile: r.status }; }).catch(function () { return { klaviyo_profile: 'error' }; })
        .then(function (p) {
          if (!sub) return p;
          return fetch(base + 'subscriptions/' + q, { method: 'POST', headers: H, body: JSON.stringify(sub) })
            .then(function (r) { p.klaviyo_subscription = r.status; return p; }).catch(function () { p.klaviyo_subscription = 'error'; return p; });
        }));
    }
    return Promise.all(jobs);
  };

  /* ---- catnip lollipop gift line (Jing 10/8; scope 10/9): while lollipop_enabled and the offer is not past offer_end_date, a cart that
     qualifies gets exactly one lollipop line. quiz_lead (default): any NL5 litter line once NL5.quizLead() (quiz finished + email &
     mobile); the store discount must make it $0 for that first order. sub_min_size: a subscription line (selling_plan) of size ≥ lollipop_min_size
     (properties _nl5_gift=lollipop, quantity 1); no qualifying line → the gift line is removed. The price goes to $0 through the
     store's subscription-purchase-type automatic BXGY (not the theme). Config: window.NL5_GIFT (quiz / PDP section, first wins;
     empty variant id = never add). Lines are addressed by key, so removals don't shift each other. Resolves true if it changed the cart. ---- */
  // 10/9 lollipop_scope quiz_lead: the visitor finished the quiz (answers saved) AND left email + mobile (quiz gate, or the PDP
  // email box before / after the quiz). Skipping the gate never counts (otherwise redoing the quiz would give one every order).
  NL5.quizLead = function () {
    var o = NL5.ls.json('nl_offer'), a = NL5.ls.json('nl_quiz');
    return !!(o && o.at && o.phone && a && a.cats && a.problem && a.current_litter);
  };
  NL5.syncGift = function () {
    var G = root.NL5_GIFT;
    if (!G || !String(G.variant_id || '').trim()) return Promise.resolve(false);
    var st = NL5.offerState({ offer_end_date: G.end, lollipop_enabled: G.enabled, lollipop_min_size: G.min_size, lollipop_scope: G.scope });
    var lead = st.scope === 'quiz_lead' && NL5.quizLead();
    if (NL5._giftBusy) return NL5._giftBusy;
    NL5._giftBusy = NL5.cartGet().then(function (cart) {
      var gifts = [], qualifies = false;
      (cart.items || []).forEach(function (it) {
        var p = it.properties || {};
        if (p._nl5_gift === 'lollipop') gifts.push(it);
        else if (st.scope === 'quiz_lead') { if (lead && /^NL-(WT|GT|CS)-/i.test(it.sku || '') && st.giftFor(NL5.sizeOf({ sku: it.sku, title: it.variant_title || '' }), { lead: true })) qualifies = true; }   // any NL5 litter line, subscription or one-time
        else if (it.selling_plan_allocation && st.giftFor(NL5.sizeOf({ sku: it.sku, title: it.variant_title || '' }))) qualifies = true;
      });
      var jobs = Promise.resolve(), changed = false;
      function chg(body) { changed = true; jobs = jobs.then(function () { return NL5.cartChange(body); }); }
      if (qualifies) {
        var gp = { _nl5_gift: 'lollipop' }; if (st.scope === 'quiz_lead') gp._nl5_gift_scope = 'quiz';   // cart line says "first order" instead of "first subscription box"
        if (!gifts.length) { changed = true; jobs = jobs.then(function () { return NL5.cartAdd([{ id: Number(G.variant_id), quantity: 1, properties: gp }]); }); }
        else {
          if (gifts[0].quantity !== 1) chg({ id: gifts[0].key, quantity: 1 });
          gifts.slice(1).forEach(function (g) { chg({ id: g.key, quantity: 0 }); });
        }
      } else gifts.forEach(function (g) { chg({ id: g.key, quantity: 0 }); });
      return jobs.then(function () { return changed; });
    }).catch(function (e) { if (NL5.debug) console.warn('[nl5] gift sync', e); return false; })
      .then(function (r) { NL5._giftBusy = null; return r; });
    return NL5._giftBusy;
  };

  if (typeof module !== 'undefined' && module.exports) module.exports = NL5;
})(typeof window !== 'undefined' ? window : globalThis);
